import { cx, focusRing } from "@/lib/utils"

export type DateRange = {
    from: Date | undefined
    to?: Date | undefined
}

export interface Preset {
    label: string
}

export interface DatePreset extends Preset {
    date: Date
}

export interface DateRangePreset extends Preset {
    dateRange: DateRange
}

export type PresetContainerProps<TPreset extends Preset, TValue> = {
    presets: TPreset[]
    onSelect: (value: TValue) => void
    currentValue?: TValue
}

const PresetContainer = <TPreset extends Preset, TValue>({
    // Available preset configurations
    presets,
    // Event handler when a preset is selected
    onSelect,
    // Currently selected preset
    currentValue,
}: PresetContainerProps<TPreset, TValue>) => {
    const isDateRangePresets = (preset: any): preset is DateRangePreset => {
        return "dateRange" in preset
    }
    const isDatePresets = (preset: any): preset is DatePreset => {
        return "date" in preset
    }

    const handleClick = (preset: TPreset) => {
        if (isDateRangePresets(preset)) {
            onSelect(preset.dateRange as TValue)
        } else if (isDatePresets(preset)) {
            onSelect(preset.date as TValue)
        }
    }

    const compareDates = (date1: Date, date2: Date) => {
        return (
            date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear()
        )
    }

    const compareRanges = (range1: DateRange, range2: DateRange) => {
        const from1 = range1.from
        const from2 = range2.from

        let equalFrom = false

        if (from1 && from2) {
            const sameFrom = compareDates(from1, from2)

            if (sameFrom) {
                equalFrom = true
            }
        }

        const to1 = range1.to
        const to2 = range2.to

        let equalTo = false

        if (to1 && to2) {
            const sameTo = compareDates(to1, to2)

            if (sameTo) {
                equalTo = true
            }
        }

        return equalFrom && equalTo
    }

    const matchesCurrent = (preset: TPreset) => {
        if (isDateRangePresets(preset)) {
            const value = currentValue as DateRange | undefined

            return value && compareRanges(value, preset.dateRange)
        }
        if (isDatePresets(preset)) {
            const value = currentValue as Date | undefined

            return value && compareDates(value, preset.date)
        }

        return false
    }

    return (
        <ul className="flex items-start gap-x-2 sm:flex-col">
            {presets.map((preset) => {
                return (
                    <li key={`preset-${preset.label}`} className="sm:w-full sm:py-px">
                        <button
                            type="button"
                            title={preset.label}
                            className={cx(
                                // base
                                "relative w-full overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none",
                                // text color
                                "text-gray-700",
                                // border color
                                "border-gray-200",
                                // focus
                                focusRing,
                                // background color
                                "focus-visible:bg-gray-100",
                                "hover:bg-gray-100",
                                {
                                    "bg-gray-100": matchesCurrent(preset),
                                },
                            )}
                            onClick={() => handleClick(preset)}
                            aria-label={`Select ${preset.label}`}
                        >
                            <span>{preset.label}</span>
                        </button>
                    </li>
                )
            })}
        </ul>
    )
}

PresetContainer.displayName = "DatePicker.PresetContainer"

export { PresetContainer }